import { dom } from "../dom";
import { type FilterState, MAX_IFC_TABS, state } from "../state";
import { setStatus } from "../ui/status";
import { escapeHtmlAttr, escapeHtmlText } from "../utils/html";
import { handleIfcFile, disposeModel } from "./loader";
import { resetCompareState } from "./compare";
import { resetFilterUiAndState } from "./filters";
import { updateAnnotationUi } from "../features/annotations";
import { setSelectionCaption, updateVisibilityButtons } from "../features/selection";
import { renderIfcTree } from "../features/tree";
import { refreshPropertiesPanel } from "../features/properties";
import { renderViewsList } from "../features/views";
import { updateSearchUiState } from "../features/search";
import { resetClippingState } from "../features/clipping";
import { refreshPlanUi } from "../features/planView";
import { clearColorRule } from "../features/colorRules";

export function getFileSignature(file: File): string {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

export function renderIfcTabs(): void {
  const tabsHtml = state.ifcTabs
    .map((tab) => {
      const activeClass = tab.id === state.activeIfcTabId ? " is-active" : "";
      const safeText = escapeHtmlText(tab.title);
      const safeAttr = escapeHtmlAttr(tab.title);
      return `<button class="ifc-tab-btn${activeClass}" type="button" data-tab-id="${tab.id}" title="${safeAttr}"><span class="ifc-tab-inner"><span class="ifc-tab-label">${safeText}</span><span class="ifc-tab-close" role="button" tabindex="-1" aria-label="Закрыть вкладку" data-close-tab-id="${tab.id}">×</span></span></button>`;
    })
    .join("");
  const plusDisabled = state.ifcTabs.length >= MAX_IFC_TABS ? "disabled" : "";
  dom.ifcTabsElement.innerHTML = `${tabsHtml}<button id="ifc-tab-plus" class="ifc-tab-btn ifc-tab-plus" type="button" ${plusDisabled} title="Открыть новый IFC">+</button>`;
}

export function hasDuplicateOpenFile(file: File, ignoreTabId: number | null = null): boolean {
  const signature = getFileSignature(file);
  return state.ifcTabs.some((tab) => tab.signature === signature && tab.id !== ignoreTabId);
}

export function resetViewerWithoutTabs(): void {
  state.currentIfcFileKey = null;
  disposeModel(state.ifcModel);
  state.ifcModel = null;
  dom.infoElement.textContent = "Файл не загружен.";
  setSelectionCaption(null);
  state.currentIfcRevision = null;
  state.ifcTree = null;
  state.modelBounds = null;
  state.savedViews.length = 0;
  resetCompareState();
  resetFilterUiAndState();
  resetClippingState();
  clearColorRule();
  updateVisibilityButtons();
  updateAnnotationUi();
  renderIfcTree();
  void refreshPropertiesPanel();
  renderViewsList();
  updateSearchUiState();
  refreshPlanUi();
  void import("../ui/publicWelcome").then((mod) => mod.showPublicWelcome());
}

export function getActiveFilterStateFromUi(): FilterState {
  return {
    storeyId: dom.filterStoreySelect.value || "",
    types: [...dom.filterTypeSelect.selectedOptions].map((opt) => opt.value).filter(Boolean),
    mark: dom.filterMarkInput.value.trim(),
  };
}

export function persistActiveTabFilterState(): void {
  if (!state.activeIfcTabId) {
    return;
  }
  const tab = state.ifcTabs.find((t) => t.id === state.activeIfcTabId);
  if (!tab) {
    return;
  }
  tab.filterState = getActiveFilterStateFromUi();
}

export async function activateIfcTab(tabId: number): Promise<void> {
  persistActiveTabFilterState();
  const tab = state.ifcTabs.find((t) => t.id === tabId);
  if (!tab) {
    return;
  }
  state.activeIfcTabId = tab.id;
  renderIfcTabs();
  await handleIfcFile(tab.file, tab.filterState || { storeyId: "", types: [], mark: "" });
}

export async function addOrReplaceIfcTab(
  file: File | undefined | null,
  mode: "new-tab" | "replace-active",
): Promise<void> {
  if (!file) {
    return;
  }
  const activeTab = state.ifcTabs.find((t) => t.id === state.activeIfcTabId) || null;
  const ignoreId = mode === "replace-active" ? (activeTab?.id ?? null) : null;
  if (hasDuplicateOpenFile(file, ignoreId)) {
    window.alert("Этот IFC файл уже открыт в приложении.");
    return;
  }

  if (mode === "replace-active" && activeTab) {
    activeTab.file = file;
    activeTab.signature = getFileSignature(file);
    activeTab.title = file.name;
    activeTab.filterState = { storeyId: "", types: [], mark: "" };
    await activateIfcTab(activeTab.id);
    setStatus(`Текущая вкладка обновлена: ${file.name}`);
    return;
  }

  if (state.ifcTabs.length >= MAX_IFC_TABS) {
    setStatus(`Можно открыть не более ${MAX_IFC_TABS} IFC файлов одновременно.`, true);
    return;
  }

  const newTab = {
    id: state.ifcTabIdCounter++,
    title: file.name,
    signature: getFileSignature(file),
    file,
    filterState: { storeyId: "", types: [], mark: "" } as FilterState,
  };
  state.ifcTabs.push(newTab);
  await activateIfcTab(newTab.id);
}

export async function closeActiveIfcTab(): Promise<void> {
  if (state.activeIfcTabId === null) {
    resetViewerWithoutTabs();
    setStatus("Сцена очищена.");
    return;
  }
  const idx = state.ifcTabs.findIndex((t) => t.id === state.activeIfcTabId);
  if (idx === -1) {
    resetViewerWithoutTabs();
    state.activeIfcTabId = null;
    renderIfcTabs();
    setStatus("Сцена очищена.");
    return;
  }
  state.ifcTabs.splice(idx, 1);
  if (state.ifcTabs.length === 0) {
    state.activeIfcTabId = null;
    renderIfcTabs();
    resetViewerWithoutTabs();
    setStatus("Вкладка закрыта. Модель выгружена.");
    return;
  }
  const nextIdx = Math.min(idx, state.ifcTabs.length - 1);
  await activateIfcTab(state.ifcTabs[nextIdx].id);
  setStatus("Вкладка IFC закрыта.");
}

export async function closeIfcTabById(tabId: number): Promise<void> {
  const idx = state.ifcTabs.findIndex((t) => t.id === tabId);
  if (idx === -1) {
    return;
  }
  if (tabId !== state.activeIfcTabId) {
    state.ifcTabs.splice(idx, 1);
    renderIfcTabs();
    setStatus("Вкладка IFC закрыта.");
    return;
  }
  await closeActiveIfcTab();
}
